import { storeToRefs } from 'pinia';
import { LayoutEvent, emit } from "@/helpers";
import { safeHtml, t } from "@/helpers/locale";
import { useRecommendations, useSettings } from "@/store";
import introJs, { Step } from "intro.js";
import { MenuDisplayOption } from '@/types';

export function useTour() {
  const recommendations = useRecommendations();
  const { settings } = storeToRefs(useSettings());
  let skipRefresh = false;
  const safeTourText = (key: string) => safeHtml(t(key));
  const steps: Step[] = [
    {
      title: safeTourText('tour.welcome.title'),
      intro: safeTourText('tour.welcome.content'),
      tooltipClass: "welcome",
    },
    {
      element: ".recommendations-table tbody tr",
      title: safeTourText('tour.recommendationList.title'),
      intro: safeTourText('tour.recommendationList.content'),
    },
    {
      element: ".recommendations-table tbody tr .checkbox-wrapper .p-checkbox",
      title: safeTourText('tour.toggleGroups.title'),
      intro: safeTourText('tour.toggleGroups.content'),
    },
    {
      element: ".recommendations-table tbody tr .details div",
      position: "left",
      title: safeTourText('tour.details.title'),
      intro: safeTourText('tour.details.content'),
    },
    {
      element: ".recommendation-group-details",
      position: "bottom",
      title: safeTourText('tour.availableUpgrades.title'),
      intro: safeTourText('tour.availableUpgrades.content'),
    },
    {
      element: ".recommendation-group-details tbody tr",
      title: safeTourText('tour.existingUpgrades.title'),
      intro: safeTourText('tour.existingUpgrades.content'),
    },
    {
      element: ".ber-ratings",
      position: "left",
      title: safeTourText('tour.berScale.title'),
      intro: safeTourText('tour.berScale.content'),
    },
    {
      element: ".estimation",
      position: "left",
      title: safeTourText('tour.estimatedCosts.title'),
      intro: safeTourText('tour.estimatedCosts.content'),
    },
    {
      element: ".loan",
      position: "left",
      title: safeTourText('tour.loan.title'),
      intro: safeTourText('tour.loan.content'),
    },

    {
      element: ".financial-detail .p-menuitem-link",
      position: "bottom",
      title: safeTourText('tour.financialDetails.title'),
      intro: safeTourText('tour.financialDetails.content'),
    },
    {
      element: ".carbon-footprint .p-menuitem-link",
      position: "bottom",
      title: safeTourText('tour.carbonFootprint.title'),
      intro: safeTourText('tour.carbonFootprint.content'),
    },
    {
      element: ".ber-map .p-menuitem-link",
      position: "bottom",
      title: safeTourText('tour.berMap.title'),
      intro: safeTourText('tour.berMap.content'),
    },
    {
      position: "bottom",
      title: safeTourText('tour.end.title'),
      intro: safeTourText('tour.end.content'),
    },
  ];

  const applyAccessibilityFixes = () => {
    // keep UL/LI semantics
    document.querySelectorAll('.introjs-bullets ul').forEach(ul => ul.removeAttribute('role'));
    document.querySelectorAll('.introjs-bullets li[role="presentation"]').forEach(li => li.removeAttribute('role')); // or li.setAttribute('role','listitem')

    // name bullets
    document.querySelectorAll('.introjs-bullets a[role="button"]')
        .forEach((a, i) => a.setAttribute('aria-label', 'Go to step ' + (i+1)));

    // name dialog
    const dlg = document.querySelector('.introjs-tooltip[role="dialog"]');
    if (dlg) {
      const t = dlg.querySelector('.introjs-tooltip-title') as HTMLElement | null;
      if (t) { t.id ||= 'introjs-tooltip-title'; dlg.setAttribute('aria-labelledby', t.id); }
      else { dlg.setAttribute('aria-label', 'Guided tour'); }
    }
  };

  function start() {
    const filteredSteps = [...steps];
    if (settings.value?.recommendationsPageOptions?.menuDisplay === MenuDisplayOption.Hidden) {
      if (filteredSteps.length >= 9) {
        filteredSteps.splice(9, 3);
      }
    } else if (settings.value?.recommendationsPageOptions?.menuDisplay instanceof Array) {
      const display = (settings.value?.recommendationsPageOptions?.menuDisplay as MenuDisplayOption[]);
      if (display.indexOf(MenuDisplayOption.FinancialDetails) == -1) {
        filteredSteps.splice(filteredSteps.indexOf(steps[9]), 1);
      }
      if (display.indexOf(MenuDisplayOption.CarbonFootprint) == -1) {
        filteredSteps.splice(filteredSteps.indexOf(steps[10]), 1);
      }
      if (display.indexOf(MenuDisplayOption.BerMap) == -1) {
        filteredSteps.splice(filteredSteps.indexOf(steps[11]), 1);
      }
    }
    
    const intro = introJs()
      .setOptions({
        steps: filteredSteps,
        keyboardNavigation: false,
        disableInteraction: true,
        nextLabel: safeTourText('tour.nextLabel'),
        prevLabel: safeTourText('tour.prevLabel')
      })
      .onbeforechange((el: HTMLElement) => {
        if (intro.currentStep() === 3) {
          return new Promise(function (resolve) {
            toggleFirstRecommendations(false);
            setTimeout(() => {
              resolve(intro);
            }, 100);
          });
        }

        // toggle recommendation before highlighting
        if (intro.currentStep() === 4) {
          return new Promise(function (resolve) {
            toggleFirstRecommendations(true);
            setTimeout(() => {
              if (!skipRefresh) {
                intro.start();
                intro.goToStepNumber(intro.currentStep() || 0);
                skipRefresh = true;
              } else {
                skipRefresh = false;
              }
              resolve(intro);
            }, 250);
          });
        }

        // toggle menu bar
        if (el.classList.contains("p-menuitem-link")) {
          return new Promise(function (resolve) {
            emit(LayoutEvent.ExpandMenuBar, true);
            setTimeout(() => resolve(intro), 100);
          });
        } else {
          emit(LayoutEvent.ExpandMenuBar, false);
        }
      }).onbeforeexit(() => {
        emit(LayoutEvent.ExpandMenuBar, false);
      });

    intro.onafterchange(applyAccessibilityFixes);
    intro.start();
  }


  function toggleFirstRecommendations(expanded: boolean) {
    const firstItem = recommendations.recommendationGroups[0];
    recommendations.expandRecommendation(firstItem, expanded)
  }

  return { start };

}
